// Shared translation layer between the one controlled school form (SchoolFormFields)
// and the several shapes the estate speaks: the AddNewIndividualSchoolPost body, the
// flat MatNewSchools input on group create, the persisted School entity, GIAS lookups
// and bulk-upload rows. Keeping every mapping here means the surfaces stay in lock-step
// and each transform is unit-testable in isolation.

import { giasCodeToBoolean, giasNurseryToBoolean } from '../constants/groupSchoolFieldOptions';

// The canonical, form-facing school shape. Strings for text inputs, three-state
// booleans (true/false/null) for the Yes/No/Not-set selects, and CustomerType carried
// as the JSON-string the multi-selects already emit.
export const emptySchoolFormValues = () => ({
  Name: '',
  Urn: '',
  NumberOfPupil: '',
  Address: '',
  Postcode: '',
  SchoolType: '',
  Country: '',
  CustomerType: '',
  LowestAge: '',
  HighestAge: '',
  LA_Code: '',
  IsBoarding: null,
  IsPost16: null,
  HasNursery: null,
  SchoolGender: '',
  ReligousCharacter: '',
  Diocese: '',
  FSMPercent: '',
  GroupCode: '',
  IsFederated: '',
  FirstName: '',
  LastName: '',
  ContactNumber: '',
  Role: '',
  // Legacy GIAS provenance strings. The three-state booleans above drive the UI, but the
  // backend still persists these raw strings (and derives the bools from them when the
  // bools are null); older reports read the string columns too. They are hidden carriers
  // — SchoolFormFields never renders them — kept flowing so we don't drop data on save.
  Boarding: '',
  Post16: '',
  Nursery: '',
});

const toIntOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = typeof value === 'number' ? value : parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

const toFloatOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = typeof value === 'number' ? value : parseFloat(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const toThreeState = (value) => (value === true || value === false ? value : null);

const toStr = (value) => (value === null || value === undefined ? '' : String(value));

// AddNewIndividualSchoolPost body (school fields only — callers add products, logo,
// MatIdVal and the subscription block). Mirrors the field set the endpoint reads today.
export const toSchoolApiPayload = (values) => ({
  Name: values.Name || '',
  FirstName: values.FirstName || '',
  LastName: values.LastName || '',
  ContactNumber: values.ContactNumber || '',
  Role: values.Role || '',
  Address: values.Address || '',
  Postcode: values.Postcode || '',
  NumberOfPupil: toIntOrNull(values.NumberOfPupil) ?? 0,
  WondeeSchoolUrn: toStr(values.Urn),
  SchoolType: values.SchoolType || '',
  CustomerType: values.CustomerType || '',
  Country: values.Country || '',
  LowestAge: toIntOrNull(values.LowestAge),
  HighestAge: toIntOrNull(values.HighestAge),
  LA_Code: toIntOrNull(values.LA_Code),
  IsBoarding: toThreeState(values.IsBoarding),
  IsPost16: toThreeState(values.IsPost16),
  HasNursery: toThreeState(values.HasNursery),
  SchoolGender: toIntOrNull(values.SchoolGender) ?? 0,
  ReligousCharacter: values.ReligousCharacter || '',
  Diocese: values.Diocese || '',
  FSMPercent: toFloatOrNull(values.FSMPercent),
  GroupCode: values.GroupCode || '',
  IsFederated: values.IsFederated || '',
  // Legacy GIAS provenance strings the backend still persists (and back-derives the
  // booleans from when they are null). Not surfaced in the form; carried through.
  Boarding: values.Boarding || '',
  Post16: values.Post16 || '',
  Nursery: values.Nursery || '',
});

// Flat MatNewSchools item for the addMatGroup request. Contact fields become the
// head-teacher fields the group-create path names.
export const toMatNewSchoolInput = (values) => ({
  Name: values.Name || '',
  Urn: toIntOrNull(values.Urn) ?? 0,
  NumberOfPupil: toIntOrNull(values.NumberOfPupil) ?? 0,
  Address: values.Address || '',
  Postcode: values.Postcode || '',
  Country: values.Country || '',
  SchoolType: values.SchoolType || '',
  IsBoarding: toThreeState(values.IsBoarding),
  IsPost16: toThreeState(values.IsPost16),
  HasNursery: toThreeState(values.HasNursery),
  ReligousCharacter: values.ReligousCharacter || '',
  Diocese: values.Diocese || '',
  FSMPercent: toFloatOrNull(values.FSMPercent),
  IsFederated: values.IsFederated || '',
  LowestAge: toIntOrNull(values.LowestAge),
  HighestAge: toIntOrNull(values.HighestAge),
  SchoolGender: toIntOrNull(values.SchoolGender) ?? 0,
  LA_Code: toIntOrNull(values.LA_Code),
  CustomerType: values.CustomerType || '',
  HeadFirstName: values.FirstName || '',
  HeadLastName: values.LastName || '',
  HeadRole: values.Role || '',
  HeadContactNumber: values.ContactNumber || '',
});

// Persisted School entity (GetInfo) -> form values, for the edit surface. GetInfo delivers
// its body in camelCase while other boundaries are PascalCase, so read either casing per
// field (same o.value ?? o.Value tolerance the duplicate/lookup boundaries use) — otherwise
// URN and the contact fields silently come through empty.
const pick = (obj, pascalKey, camelKey) => {
  const p = obj[pascalKey];
  if (p !== undefined && p !== null && p !== '') return p;
  const c = obj[camelKey];
  return c !== undefined && c !== null ? c : undefined;
};

export const fromApiSchool = (school = {}) => ({
  ...emptySchoolFormValues(),
  Name: pick(school, 'Name', 'name') || '',
  Urn: pick(school, 'WondeeSchoolUrn', 'wondeeSchoolUrn') ? String(pick(school, 'WondeeSchoolUrn', 'wondeeSchoolUrn')) : '',
  NumberOfPupil: pick(school, 'NumberOfPupil', 'numberOfPupil') ? String(pick(school, 'NumberOfPupil', 'numberOfPupil')) : '',
  Address: pick(school, 'Address', 'address') || '',
  Postcode: pick(school, 'Postcode', 'postcode') || '',
  SchoolType: pick(school, 'SchoolType', 'schoolType') || '',
  Country: pick(school, 'Country', 'country') || '',
  CustomerType: pick(school, 'CustomerType', 'customerType') || '',
  LowestAge: pick(school, 'LowestAge', 'lowestAge') ? String(pick(school, 'LowestAge', 'lowestAge')) : '',
  HighestAge: pick(school, 'HighestAge', 'highestAge') ? String(pick(school, 'HighestAge', 'highestAge')) : '',
  LA_Code: pick(school, 'LA_Code', 'la_Code') ? String(pick(school, 'LA_Code', 'la_Code')) : '',
  IsBoarding: toThreeState(pick(school, 'IsBoarding', 'isBoarding')),
  IsPost16: toThreeState(pick(school, 'IsPost16', 'isPost16')),
  HasNursery: toThreeState(pick(school, 'HasNursery', 'hasNursery')),
  SchoolGender: (() => {
    const g = pick(school, 'SchoolGender', 'schoolGender');
    return g !== undefined && g !== null ? g : '';
  })(),
  ReligousCharacter: pick(school, 'ReligousCharacter', 'religousCharacter') || '',
  Diocese: pick(school, 'Diocese', 'diocese') || '',
  FSMPercent: pick(school, 'FSMPercent', 'fsmPercent') ? String(pick(school, 'FSMPercent', 'fsmPercent')) : '',
  GroupCode: pick(school, 'GroupCode', 'groupCode') || '',
  IsFederated: pick(school, 'IsFederated', 'isFederated') || '',
  FirstName: pick(school, 'FirstName', 'firstName') || '',
  LastName: pick(school, 'LastName', 'lastName') || '',
  ContactNumber: pick(school, 'ContactNumber', 'contactNumber') || '',
  Role: pick(school, 'Role', 'role') || '',
  Boarding: pick(school, 'Boarding', 'boarding') || '',
  Post16: pick(school, 'Post16', 'post16') || '',
  Nursery: pick(school, 'Nursery', 'nursery') || '',
});

const GIAS_GENDER_MAP = { 1: 1, 2: 2, 3: 3 };

// GIAS lookup payload -> form values. Booleans derive via the shared GIAS helpers, and
// the two UK-only text fields carry GIAS's own defaults when it has no answer, exactly
// as the modal did before. Returns a full form-shaped object; callers decide whether to
// overwrite non-empty fields.
export const fromGias = (gias = {}) => {
  const genderCode = gias.Gender_code !== null && gias.Gender_code !== undefined ? String(gias.Gender_code) : '';
  const address = `${gias.Street || ''}, ${gias.Locality || ''}, ${gias.Town || ''}, ${gias.County_name || ''}`
    .replace(/^,\s*|,\s*$/g, '');

  return {
    ...emptySchoolFormValues(),
    Name: gias.EstablishmentName || '',
    Urn: gias.URN ? String(gias.URN) : '',
    NumberOfPupil: gias.NumberOfPupils ? String(gias.NumberOfPupils) : '',
    Address: address,
    Postcode: gias.Postcode || '',
    SchoolType: gias.TypeOfEstablishment_name || '',
    LowestAge: toIntOrNull(gias.StatutoryLowAge) !== null ? String(toIntOrNull(gias.StatutoryLowAge)) : '',
    HighestAge: toIntOrNull(gias.StatutoryHighAge) !== null ? String(toIntOrNull(gias.StatutoryHighAge)) : '',
    LA_Code: toIntOrNull(gias.LA_code) !== null ? String(toIntOrNull(gias.LA_code)) : '',
    IsBoarding: giasCodeToBoolean(gias.Boarders_code),
    IsPost16: giasCodeToBoolean(gias.OfficialSixthForm_code),
    HasNursery: giasNurseryToBoolean(gias.NurseryProvision_name),
    // Carry the raw GIAS provenance strings alongside the derived booleans, as the old
    // add flow did — the backend still stores them and derives bools from them.
    Boarding: gias.Boarders_code || '',
    Post16: gias.OfficialSixthForm_code || '',
    Nursery: gias.NurseryProvision_name || '',
    SchoolGender: GIAS_GENDER_MAP[genderCode] !== undefined ? GIAS_GENDER_MAP[genderCode] : '',
    ReligousCharacter: gias.ReligiousCharacter_name || 'Does Not Apply',
    Diocese: gias.Diocese_name || 'Not Applicable',
    FSMPercent: toFloatOrNull(gias.PercentageFSM) !== null ? String(toFloatOrNull(gias.PercentageFSM)) : '',
    GroupCode: gias.Trusts_code || '',
    IsFederated: gias.FederationFlag_name || '',
    FirstName: gias.HeadFirstName || '',
    LastName: gias.HeadLastName || '',
    ContactNumber: gias.TelephoneNum || '',
    Role: gias.HeadPreferredJobTitle || '',
  };
};

// A parsed bulk-upload row's `fields` object -> form values, so file-sourced schools
// join the wizard list on the same footing as GIAS/manual ones.
export const fromBulkRow = (fields = {}) => ({
  ...emptySchoolFormValues(),
  Name: fields.name || '',
  Urn: fields.urn ? String(fields.urn) : '',
  // Guard on null/undefined, not falsiness: a pupil count of 0 is a real value and must
  // survive, or the row fails the wizard's required-field gate and blocks the upload.
  NumberOfPupil: fields.numberOfPupil !== null && fields.numberOfPupil !== undefined ? String(fields.numberOfPupil) : '',
  Address: fields.address || '',
  Postcode: fields.postcode || '',
  Country: fields.country || '',
  SchoolType: fields.schoolType || '',
  IsBoarding: toThreeState(fields.isBoarding),
  IsPost16: toThreeState(fields.isPost16),
  HasNursery: toThreeState(fields.hasNursery),
  ReligousCharacter: fields.religousCharacter || '',
  Diocese: fields.diocese || '',
  FSMPercent: fields.fsmPercent !== null && fields.fsmPercent !== undefined ? String(fields.fsmPercent) : '',
  IsFederated: toThreeState(fields.isFederated) === null ? (fields.isFederated || '') : String(fields.isFederated),
});

// "Add another like this": keep the classification the operator just set (type, country,
// the three-state characteristics, the UK-only fields and the customer type) but wipe the
// identity of the individual school so they start a fresh record.
export const prefillFromPrevious = (values) => ({
  ...emptySchoolFormValues(),
  SchoolType: values.SchoolType || '',
  Country: values.Country || '',
  CustomerType: values.CustomerType || '',
  LowestAge: values.LowestAge || '',
  HighestAge: values.HighestAge || '',
  LA_Code: values.LA_Code || '',
  IsBoarding: toThreeState(values.IsBoarding),
  IsPost16: toThreeState(values.IsPost16),
  HasNursery: toThreeState(values.HasNursery),
  SchoolGender: values.SchoolGender === null || values.SchoolGender === undefined ? '' : values.SchoolGender,
  ReligousCharacter: values.ReligousCharacter || '',
  Diocese: values.Diocese || '',
  GroupCode: values.GroupCode || '',
  IsFederated: values.IsFederated || '',
  // Classification provenance, not identity — keep it on "add another like this".
  Boarding: values.Boarding || '',
  Post16: values.Post16 || '',
  Nursery: values.Nursery || '',
});
